import { KSAIcon, profileIcon } from "assets/icons";
import { userProfilePlaceholder } from "assets/images";
import moment from "moment-timezone";
import { FaFilePdf } from "react-icons/fa";
import { IoDocumentText } from "react-icons/io5";
import { TbFileTypeDocx } from "react-icons/tb";

export default function encryptedMultipartDataHelper(formData, sendData) {
  for (const key in sendData) {
    const value = sendData[key];

    if (value && typeof value === "object") {
      // Check if the value is an object or array, stringify before appending
      formData.append(key, JSON.stringify(value));
    } else {
      // Append primitive data types directly
      formData.append(key, value);
    }
  }

  return formData;
}

export const previewDocIcon = (type) => {
  const docType = type.toLowerCase(); // Normalize to lowercase for case-insensitive matching

  const docLogo = {
    pdf: <FaFilePdf className="pdf_icon" />,
    doc: <IoDocumentText />,
    docx: <TbFileTypeDocx />,
  }[docType] || <FaFilePdf />; // Default to PDF icon if no match is found

  return docLogo;
};

// Utility to get the file extension from a file name
export const getFileExtension = (fileName) => {
  if (!fileName) return null;

  const extension = fileName.split(".").pop()?.toLowerCase(); // Get the last part after the dot and normalize to lowercase
  return extension || null; // Return null if no extension is found
};

export const showImageHandler = (src) => {
  if (!src) return null;

  const extension = getFileExtension(src); // Extract the file extension

  if (["png", "jpg", "jpeg"].includes(extension)) {
    // If the extension is an image format, return an image element
    return <img src={src} alt="file preview" className="cstm_style_img" />;
  }

  // If not an image, return an icon based on the file type
  return previewDocIcon(extension);
};

export const convertToLowerCase = (value) => {
  return value.toLowerCase();
};

export const capitalizeFirstLetter = (value) => {
  if (typeof value !== "string" || value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const convertToUpperCase = (value) => {
  return value.toUpperCase();
};

export const isArray = (value) => {
  return Array.isArray(value) ? value : [value];
};

export const validateOnlyNumber = (mobile) => {
  // Remove all non-numeric characters
  const cleanedMobile = mobile.replace(/\D/g, "");

  // If the result is an empty string, return a placeholder
  return cleanedMobile || ""; // Replace '' with any placeholder if desired
};

// Utility function
export const getValueOrFallback = (value, placeholder = "---") => {
  return value ?? placeholder;
};

// profile Image handler function
export const profileImageHandler = (userGender) => {
  const profileImage = {
    male: KSAIcon,
    female: profileIcon,
    other: userProfilePlaceholder,
  }[userGender || "other"];

  return profileImage;
};

export const removeEmptyValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) => value !== undefined && value !== null && value !== ""
    )
  );
};

export const formatArrayWithSeparator = (array, separator = ",") => {
  return isArray(array) ? array.join(separator) : array;
};

export const formatMultiSelectArray = (
  array,
  separator = ",",
  inArray = true,
  show = "label"
) => {
  if (!Array.isArray(array) || array.length === 0) return null;
  const formattedValue = array.map((item) => item?.[show] || "");
  return inArray ? formattedValue : formattedValue.join(separator);
};

export const handleDynamicList = ({
  arrayList = [],
  labelKey = "name",
  valueKey = "_id",
  sourcekey = "label",
  sourceValue = "value",
}) => {
  return arrayList?.map((data) => ({
    [sourcekey]: data?.[labelKey],
    [sourceValue]: data?.[valueKey],
  }));
};

// export const isTrue = (value) => {
//   return value === true || value === "true";
// };

export const isTrue = (value) => {
  switch (typeof value) {
    case "boolean":
      return value;
    case "string":
      return new Set(["true", "1", "yes", "on"]).has(
        value.toLowerCase().trim()
      );
    case "number":
      return value !== 0;
    default:
      return false;
  }
};

export const isFalse = (value) => {
  switch (typeof value) {
    case "boolean":
      return !value;
    case "string":
      return new Set(["false", "0", "no", "off"]).has(
        value.toLowerCase().trim()
      );
    case "number":
      return value === 0;
    default:
      return false;
  }
};

export const scrollPage = (direction = "up") => {
  const scrollAmount =
    direction === "down" ? document.documentElement.scrollHeight : 0;

  if (scrollAmount !== window.scrollY) {
    window.scrollTo({
      top: scrollAmount,
      behavior: "smooth",
    });
  }
};

export const scrollPage1 = (direction = "up", elementRef = null) => {
  if (elementRef?.current) {
    elementRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  } else {
    const scrollAmount =
      direction === "down" ? document.documentElement.scrollHeight : 0;

    if (scrollAmount !== window.scrollY) {
      window.scrollTo({
        top: scrollAmount,
        behavior: "smooth",
      });
    }
  }
};

export const isObjectEmpty = (obj) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const isArrayEmpty = (array) => {
  return Array.isArray(array) && array.length === 0;
};

export const adjustOptions = (key, array) => {
  return array.map((option, index) => ({
    ...option,
    [key]: index + 1, // Reassign the key value starting from 1
  }));
};

// Function to convert HTML to plain text
export const stripHtmlAndStyles = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

// Function to apply character limit without counting HTML tags or styles
export const charLimit = (html, limit) => {
  const plainText = stripHtmlAndStyles(html); // Remove HTML tags and styles
  return plainText?.length > limit
    ? plainText.substring(0, limit + 1) + "..." || "---"
    : plainText;
};

// Transfrom from array to object

export function transformArrayToObject(
  array,
  keyField = "value",
  valueField = "label"
) {
  return array.reduce((acc, item) => {
    acc[item[keyField]] = item[valueField];
    return acc;
  }, {});
}

export const renderDynamicTag = ({
  tag,
  className,
  value,
  separator = " ",
}) => {
  const Tag = tag;
  const content = Array.isArray(value) ? value.join(separator) : value;
  return <Tag className={className}>{content}</Tag>;
};

export const getValuesByKey = (arr, key) => {
  return arr.map((item) => item[key]);
};

export const isValidValue = (value) => {
  // Check for undefined or null

  if (value === undefined || value === null || value === "") {
    return false;
  }

  return true;
};

export function capitalizeFirstWord(str) {
  return str.replace(/^\s*\w/, (match) => match.toUpperCase());
}

// Function to generate marks dynamically
export const generateSliderMarks = (min, max, step) => {
  const markValue = Array.from(
    { length: (max - min) / step + 1 },
    (_, i) => min + i * step
  ).reduce((acc, val) => {
    acc[val] = val;
    return acc;
  }, {});

  return markValue;
};

// Function to calculate dynamic step
export const calculateStep = (min, max, totalSteps = 5) => {
  if (totalSteps <= 1 || max <= min) {
    return 1;
  }
  return (max - min) / totalSteps;
};

export const findSerialNumber = (index = 0, activePage = 1, limit = 10) => {
  return (activePage - 1) * limit + index + 1;
};

export const getMessageLabel = (date) => {
  const messageDate = moment(date);
  const now = moment();
  const daysDiff = now.diff(messageDate, "days");

  switch (true) {
    case daysDiff === 0:
      return "Today";
    case daysDiff === 1:
      return "Yesterday";
    case daysDiff < 7:
      return messageDate.format("dddd"); // Returns weekday name for recent messages
    default:
      return messageDate.format("DD/MM/YYYY"); // Returns full date for older messages
  }
};
